import React, { useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from '../../util/reactIntl';
import {
  InlineTextButton,
  FieldTextInput,
  FieldCurrencyInput,
  FieldSelect,
  FieldCheckboxGroup,
  PrimaryButton,
  SecondaryButton,
  Modal,
  Icon,
} from '../../components';
import * as validators from '../../util/validators';
import config from '../../config';
import classNames from 'classnames';

import css from './EditLiveRoomMembershipForm.module.css';

const MIN_DURATION = 1;

/**
 * Helper function to help with formatting duration input which
 * shouldn't allow negative values.
 */
const durationFieldFormatted = durationNumber => {
  const positiveDurationNumber = durationNumber ? Math.abs(durationNumber) : null;
  return positiveDurationNumber;
};

const EditLiveRoomMembershipsField = props => {
  const {
    intl,
    push,
    isMembershipView,
    membershipsFromValues,
    activeMembershipIndex,
    setActiveMembershipIndex,
    availabilityExceptions = [],
    form,
  } = props;

  const [deletionWarning, setDeletionWarning] = useState(null);

  // name
  const nameMessage = intl.formatMessage({ id: 'EditLiveRoomMembershipForm.name' });
  const namePlaceholder = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.namePlaceholder',
  });
  const nameRequiredMessage = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.nameRequired',
  });
  const nameRequired = validators.required(nameRequiredMessage);

  // description
  const descriptionMessage = intl.formatMessage({ id: 'EditLiveRoomMembershipForm.description' });
  const descriptionPlaceholder = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.descriptionPlaceholder',
  });
  const descriptionRequiredMessage = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.descriptionRequired',
  });
  const descriptionRequired = validators.required(descriptionRequiredMessage);

  // price
  const priceMessage = intl.formatMessage({ id: 'EditLiveRoomMembershipForm.price' });
  const pricePlaceholder = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.pricePlaceholder',
  });
  const priceRequiredMessage = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.priceRequired',
  });
  // Stripe's smallest accepted charge is ~$0.50 in any major currency.
  // Anything below that would fail at checkout with an "Amount must be
  // at least..." error from Stripe, so we block it here at edit time.
  const STRIPE_MIN_SUBUNITS = 50;
  const priceTooLowMessage = intl.formatMessage(
    { id: 'EditLiveRoomMembershipForm.priceTooLow' },
    { min: '0.50' }
  );
  const priceRequired = validators.composeValidators(
    validators.required(priceRequiredMessage),
    validators.moneySubUnitAmountAtLeast(priceTooLowMessage, STRIPE_MIN_SUBUNITS)
  );
  const priceHelpText = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.priceHelp',
  });

  // duration
  const durationMessage = intl.formatMessage({ id: 'EditLiveRoomMembershipForm.duration' });
  const durationLengthPlaceholder = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.durationLengthPlaceholder',
  });
  const durationLengthRequiredMessage = intl.formatMessage({
    id: 'EditLiveRoomMembershipForm.durationLengthRequired',
  });
  const durationLengthRequired = validators.required(durationLengthRequiredMessage);

  // perks
  const perksMessage = intl.formatMessage({ id: 'EditLiveRoomMembershipForm.perks' });

  // Check if membership has dependent timeslots
  const checkMembershipDependencies = membershipId => {
    const dependentTimeslots = availabilityExceptions.filter(
      exception => exception.attributes?.membershipTierId === membershipId
    );

    return {
      hasDependencies: dependentTimeslots.length > 0,
      dependentCount: dependentTimeslots.length,
      dependentTimeslots,
    };
  };

  // Handle membership deletion with dependency check
  const handleDeleteMembership = (index, membershipId) => {
    if (!membershipId) {
      // New membership without ID, can delete directly
      form.mutators.remove('memberships', index);
      if (activeMembershipIndex === index) {
        setActiveMembershipIndex(null);
      }
      return;
    }

    const dependencies = checkMembershipDependencies(membershipId);

    if (dependencies.hasDependencies) {
      setDeletionWarning({
        isOpen: true,
        membershipId,
        membershipIndex: index,
        membershipName: membershipsFromValues[index]?.name,
        dependentCount: dependencies.dependentCount,
        dependentTimeslots: dependencies.dependentTimeslots,
      });
    } else {
      // Safe to delete
      form.mutators.remove('memberships', index);
      if (activeMembershipIndex === index) {
        setActiveMembershipIndex(null);
      }
    }
  };

  // Force delete membership and clean up timeslots
  const handleForceDeleteMembership = () => {
    if (deletionWarning) {
      form.mutators.remove('memberships', deletionWarning.membershipIndex);
      if (activeMembershipIndex === deletionWarning.membershipIndex) {
        setActiveMembershipIndex(null);
      }
      setDeletionWarning(null);
    }
  };

  return (
    <>
      <FieldArray name="memberships">
        {({ fields }) => {
          // Layout if there aren't any memberships added
          const membershipsCount = membershipsFromValues ? membershipsFromValues.length : 0;
          if (membershipsCount === 0) {
            return (
              <div className={css.emptyState}>
                <h3 className={css.emptyStateTitle}>
                  <FormattedMessage id="EditLiveRoomMembershipForm.emptyStateTitle" />
                </h3>
                <p className={css.emptyStateText}>
                  <FormattedMessage id="EditLiveRoomMembershipForm.emptyStateText" />
                </p>
                <p className={css.emptyStateReassurance}>
                  <FormattedMessage id="EditLiveRoomMembershipForm.emptyStateReassurance" />
                </p>
              </div>
            );
          }

          return (
            <div className={css.memberships}>
              {fields.map((fieldName, index) => {
                const { name } = membershipsFromValues[index] || {};

                const handleActiveMembershipClick = () => {
                  const activeIndex = activeMembershipIndex === index ? null : index;
                  setActiveMembershipIndex(activeIndex);
                };

                const activeMembership = activeMembershipIndex === index;
                const headerClasses = classNames(css.membershipHeader, {
                  [css.hiddenMembershipHeader]: activeMembership,
                });
                const formClasses = classNames(css.membershipForm, {
                  [css.visibleMembershipForm]: activeMembership,
                });

                // Remove all other memberships from layout, if we're inside
                // active membership view (e.g. editing membership).
                if (isMembershipView && !activeMembership) {
                  return null;
                }

                const renderName = name ? (
                  name
                ) : (
                  <FormattedMessage
                    id="EditLiveRoomMembershipForm.membershipTitle"
                    values={{ index: index + 1 }}
                  />
                );

                return (
                  <div className={css.membership} key={fieldName}>
                    <div className={headerClasses}>
                      <h3 className={css.membershipTitle}>{renderName}</h3>
                      <div className={css.manage}>
                        <InlineTextButton
                          type="button"
                          className={css.manageButton}
                          onClick={handleActiveMembershipClick}
                        >
                          <Icon name="edit" size={20} className={css.manageButtonIcon} />
                        </InlineTextButton>
                        <InlineTextButton
                          type="button"
                          className={css.manageButton}
                          onClick={() =>
                            handleDeleteMembership(index, membershipsFromValues[index]?.id)
                          }
                        >
                          <Icon name="delete" size={20} className={css.manageButtonIcon} />
                        </InlineTextButton>
                      </div>
                    </div>
                    <div className={formClasses}>
                      <FieldTextInput
                        id={`${fieldName}.name`}
                        name={`${fieldName}.name`}
                        type="text"
                        label={nameMessage}
                        placeholder={namePlaceholder}
                        validate={nameRequired}
                      />
                      <FieldTextInput
                        id={`${fieldName}.description`}
                        name={`${fieldName}.description`}
                        className={css.field}
                        type="textarea"
                        label={descriptionMessage}
                        placeholder={descriptionPlaceholder}
                        validate={descriptionRequired}
                      />
                      <FieldCurrencyInput
                        id={`${fieldName}.price`}
                        name={`${fieldName}.price`}
                        className={css.field}
                        label={priceMessage}
                        placeholder={pricePlaceholder}
                        currencyConfig={config.currencyConfig}
                        validate={priceRequired}
                      />
                      <p className={css.priceHelp}>{priceHelpText}</p>
                      <div className={css.field}>
                        <label>{durationMessage}</label>
                        <div className={css.durationFields}>
                          <FieldTextInput
                            id={`${fieldName}.duration.length`}
                            name={`${fieldName}.duration.length`}
                            className={css.durationLengthField}
                            type="number"
                            min={MIN_DURATION}
                            placeholder={durationLengthPlaceholder}
                            format={durationFieldFormatted}
                            validate={durationLengthRequired}
                          />
                          <FieldSelect
                            id={`${fieldName}.duration.type`}
                            name={`${fieldName}.duration.type`}
                            className={css.durationTypeField}
                            options={config.custom.durationConfig}
                            defaultValue={config.custom.durationConfig[0].key}
                          />
                        </div>
                      </div>
                      <FieldCheckboxGroup
                        id={`${fieldName}.perks`}
                        name={`${fieldName}.perks`}
                        rootClassName={css.fieldCheckboxGroup}
                        label={perksMessage}
                        options={config.custom.perksConfig}
                        twoColumns
                      />
                    </div>
                  </div>
                );
              })}
              {isMembershipView ? (
                <div className={css.actionButtons}>
                  <SecondaryButton
                    className={css.actionButton}
                    type="button"
                    onClick={() =>
                      handleDeleteMembership(
                        activeMembershipIndex,
                        membershipsFromValues[activeMembershipIndex]?.id
                      )
                    }
                  >
                    <FormattedMessage id="EditLiveRoomMembershipForm.deleteButton" />
                  </SecondaryButton>
                  <PrimaryButton
                    className={classNames(css.actionButton, css.actionButtonSave)}
                    type="button"
                    onClick={() => setActiveMembershipIndex(null)}
                  >
                    <FormattedMessage id="EditLiveRoomMembershipForm.saveButton" />
                  </PrimaryButton>
                </div>
              ) : null}
            </div>
          );
        }}
      </FieldArray>

      {/* Deletion Warning Modal */}
      {deletionWarning && (
        <Modal
          id="MembershipDeletionWarning"
          isOpen={deletionWarning.isOpen}
          onClose={() => setDeletionWarning(null)}
          onManageDisableScrolling={() => {}}
          containerClassName={css.modalContainer}
          contentClassName={css.modalContent}
        >
          <div>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>
                <FormattedMessage id="EditLiveRoomMembershipForm.deletionWarningTitle" />
              </h2>
              <p className={css.warningMessage}>
                <FormattedMessage
                  id="EditLiveRoomMembershipForm.deletionWarningMessage"
                  values={{
                    membershipName: deletionWarning.membership?.name || '',
                    count: deletionWarning.dependentCount,
                  }}
                />
              </p>
            </div>

            <div className={css.affectedTimeslots}>
              <h3>
                <FormattedMessage id="EditLiveRoomMembershipForm.affectedTimeslots" />
              </h3>
              <ul className={css.timeslotList}>
                {deletionWarning.dependentTimeslots.map(timeslot => (
                  <li key={timeslot.id || timeslot.attributes?.id}>
                    {timeslot.attributes?.title ||
                      intl.formatMessage(
                        { id: 'EditLiveRoomMembershipForm.timeslotDate' },
                        { date: new Date(timeslot.attributes?.start).toLocaleDateString() }
                      )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EditLiveRoomMembershipsField;
